// SOURCE OF TRUTH: keep in sync with journal-dashboard/src/styles/sproutTokens.ts
// Ported from AutoCalc core-autocalc-ui/src/mui/naColors.ts (MUI bits stripped).
// The Sprout design system tokens — raw palette + semantic light/dark token sets.

/** Sprout raw palette — exact brand hexes. */
export const sproutPalette = {
  green: {
    50: '#F3FDF6', 100: '#E2F9EA', 200: '#C0F0D1', 300: '#8DD9A8', 400: '#57B370',
    500: '#2C9C55', 600: '#00843D', // Cargill Leaf Green (primary)
    700: '#01632D', 800: '#03441F', 900: '#023719', 950: '#012912', 1000: '#000000',
  },
  softGreen: {
    50: '#F5F9ED', 100: '#E7F4D4', 200: '#D9EFBB', 300: '#CBEAA1', 400: '#BDE588',
    500: '#9FC864', 600: '#81AB40', 700: '#618030', 800: '#475E23', 900: '#32421A',
  },
  neutral: {
    10: '#FFFFFF', 50: '#F8F8F8', 100: '#F3F4F3', 200: '#E7E8E8', 300: '#DBDDDC',
    350: '#C3C6C5', 400: '#9FA4A2', 500: '#707773', 600: '#58605C', 700: '#404945',
    800: '#28332D', 900: '#1C2722', 1000: '#101C16',
  },
  red: {
    50: '#FEE2E2', 100: '#FECACA', 200: '#FCA5A5', 300: '#F87171', 400: '#EB5655',
    500: '#DC3737', 600: '#C50F1F', 700: '#A80D19', 800: '#8B0A15', 900: '#6E0810',
  },
  yellow: {
    50: '#FFFDF0', 100: '#FFF8D7', 200: '#FEEEA3', 300: '#FED96A', 400: '#FEC230',
    500: '#FEA800', 600: '#E08C00', 700: '#C27200', 800: '#A45A00', 900: '#7A3F00',
  },
  blue: {
    50: '#F5FBFF', 100: '#E0F2FF', 200: '#C0E3FD', 300: '#8DC5F6', 400: '#55A5EB',
    500: '#3683D8', 600: '#2168CE', 700: '#0F49C5', 800: '#0C3AA0', 900: '#092B7A',
  },
} as const;

const g = sproutPalette.green;
const sg = sproutPalette.softGreen;
const n = sproutPalette.neutral;
const r = sproutPalette.red;
const y = sproutPalette.yellow;
const b = sproutPalette.blue;

/** Shape of every token set. */
export interface DesignTokens {
  bg: string;
  surface: string;
  surfaceAlt: string;
  surfaceDark: string;
  surfaceHover: string;
  surfaceActive: string;
  border: string;
  borderSubtle: string;
  borderHover: string;
  primary: string;
  primaryHover: string;
  primaryBg: string;
  primaryLight: string;
  success: string;
  successDark: string;
  successBg: string;
  error: string;
  errorBg: string;
  warning: string;
  warningBg: string;
  info: string;
  infoBg: string;
  purple: string;
  purpleBg: string;
  textPrimary: string;
  textBody: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  textDisabled: string;
  textOnPrimary: string;
  disabled: string;
  skeleton: string;
  skeletonHighlight: string;
  shadow: string;
  shadowLight: string;
  chatUserBubble: string;
  chatUserBorder: string;
  chatBotBubble: string;
  chatTimestamp: string;
}

/* ── Light tokens (Sprout light mode) ─────────────────────────── */
export const lightTokens: DesignTokens = {
  bg:            sg[50],   // #F5F9ED
  surface:       n[10],    // #FFFFFF
  surfaceAlt:    n[50],    // #F8F8F8
  surfaceDark:   n[100],   // #F3F4F3
  surfaceHover:  n[100],
  surfaceActive: sg[100],
  border:        n[300],
  borderSubtle:  n[200],
  borderHover:   n[400],
  primary:       g[600],   // #00843D
  primaryHover:  g[700],
  primaryBg:     g[100],
  primaryLight:  sg[400],
  success:       g[600],
  successDark:   g[700],
  successBg:     g[100],
  error:         r[600],
  errorBg:       r[50],
  warning:       y[500],
  warningBg:     y[100],
  info:          b[700],
  infoBg:        b[50],
  purple:        '#7C3AED',
  purpleBg:      '#EDE9FE',
  textPrimary:   n[1000],
  textBody:      n[700],
  textSecondary: n[600],
  textTertiary:  n[500],
  textMuted:     n[500],
  textDisabled:  n[400],
  textOnPrimary: n[10],
  disabled:      n[400],
  skeleton:      n[200],
  skeletonHighlight: 'rgba(255,255,255,0.85)',
  shadow:        'rgba(16,28,22,0.08)',
  shadowLight:   'rgba(16,28,22,0.04)',
  chatUserBubble: sg[100],
  chatUserBorder: sg[400],
  chatBotBubble:  n[50],
  chatTimestamp:  'rgba(16,28,22,0.45)',
};

/* ── Dark tokens (Sprout dark mode) — brand green kept in dark too ── */
export const darkTokens: DesignTokens = {
  bg:            n[900],   // #1C2722
  surface:       n[1000],  // #101C16
  surfaceAlt:    n[800],   // #28332D
  surfaceDark:   n[1000],
  surfaceHover:  n[800],
  surfaceActive: g[950],   // #012912
  border:        n[700],   // #404945
  borderSubtle:  n[800],
  borderHover:   n[600],
  primary:       g[600],   // #00843D
  primaryHover:  g[700],
  primaryBg:     g[950],
  primaryLight:  sg[400],
  success:       g[600],
  successDark:   g[700],
  successBg:     g[950],
  error:         r[400],   // #EB5655
  errorBg:       r[900],
  warning:       y[500],
  warningBg:     y[900],
  info:          b[400],   // #55A5EB
  infoBg:        b[900],
  purple:        '#A78BFA',
  purpleBg:      '#1E1535',
  textPrimary:   n[10],    // #FFFFFF
  textBody:      n[300],   // #DBDDDC
  textSecondary: n[400],   // #9FA4A2
  textTertiary:  n[500],   // #707773
  textMuted:     n[400],
  textDisabled:  n[500],
  textOnPrimary: n[10],
  disabled:      n[500],
  skeleton:      n[700],
  skeletonHighlight: 'rgba(255,255,255,0.12)',
  shadow:        'rgba(0,0,0,0.3)',
  shadowLight:   'rgba(0,0,0,0.2)',
  chatUserBubble: g[950],
  chatUserBorder: sg[400],
  chatBotBubble:  n[800],
  chatTimestamp:  'rgba(255,255,255,0.55)',
};

/**
 * CSS-variable contract. Maps each `--dt-*` custom property to its DesignTokens key.
 * The ThemeProvider iterates this to write vars onto <html> for the active mode;
 * tailwind.config.js reads the same `--dt-*` names. Single source of the var names.
 */
export const DT_VAR_MAP: Record<string, keyof DesignTokens> = {
  '--dt-bg': 'bg',
  '--dt-surface': 'surface',
  '--dt-surface-alt': 'surfaceAlt',
  '--dt-surface-dark': 'surfaceDark',
  '--dt-surface-hover': 'surfaceHover',
  '--dt-surface-active': 'surfaceActive',
  '--dt-border': 'border',
  '--dt-border-subtle': 'borderSubtle',
  '--dt-border-hover': 'borderHover',
  '--dt-primary': 'primary',
  '--dt-primary-hover': 'primaryHover',
  '--dt-primary-bg': 'primaryBg',
  '--dt-primary-light': 'primaryLight',
  '--dt-success': 'success',
  '--dt-success-dark': 'successDark',
  '--dt-success-bg': 'successBg',
  '--dt-error': 'error',
  '--dt-error-bg': 'errorBg',
  '--dt-warning': 'warning',
  '--dt-warning-bg': 'warningBg',
  '--dt-info': 'info',
  '--dt-info-bg': 'infoBg',
  '--dt-purple': 'purple',
  '--dt-purple-bg': 'purpleBg',
  '--dt-text-primary': 'textPrimary',
  '--dt-text-body': 'textBody',
  '--dt-text-secondary': 'textSecondary',
  '--dt-text-tertiary': 'textTertiary',
  '--dt-text-muted': 'textMuted',
  '--dt-text-disabled': 'textDisabled',
  '--dt-text-on-primary': 'textOnPrimary',
  '--dt-disabled': 'disabled',
  '--dt-skeleton': 'skeleton',
  '--dt-skeleton-highlight': 'skeletonHighlight',
  '--dt-shadow': 'shadow',
  '--dt-shadow-light': 'shadowLight',
  '--dt-chat-user-bubble': 'chatUserBubble',
  '--dt-chat-user-border': 'chatUserBorder',
  '--dt-chat-bot-bubble': 'chatBotBubble',
  '--dt-chat-timestamp': 'chatTimestamp',
};

export type ThemeMode = 'light' | 'dark';
export const tokensFor = (mode: ThemeMode): DesignTokens => (mode === 'dark' ? darkTokens : lightTokens);
